(() => {
  if (window.LSSoilTemperature) return;

  const mean = (values) => values.reduce((sum, value) => sum + value, 0) / values.length;
  const normalizeZip = (value) => String(value || '').replace(/\D/g, '').slice(0, 5);
  const soilApiEndpoint = 'https://lawn-synergy-soil-api.vercel.app/api/soil-temperature';
  const pending = new Map();

  const geocodeZip = async (value) => {
    const postalCode = normalizeZip(value);
    if (postalCode.length !== 5) throw new Error('Enter a valid U.S. ZIP code.');

    // Open-Meteo's geocoder is place-name based and does not reliably resolve
    // U.S. ZIP codes (for example, 63126). Use a postal-code-specific source.
    const response = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(postalCode)}`);
    if (response.status === 404) throw new Error('Enter a valid U.S. ZIP code.');
    if (!response.ok) throw new Error('Location lookup is temporarily unavailable.');

    const data = await response.json();
    const place = data.places?.[0];
    const latitude = Number(place?.latitude);
    const longitude = Number(place?.longitude);
    if (!place || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new Error('Enter a valid U.S. ZIP code.');
    }

    return {
      zip: postalCode,
      latitude,
      longitude,
      city: place['place name'] || '',
      state: place.state || ''
    };
  };

  const getSoilData = async (location) => {
    const url = new URL(soilApiEndpoint);
    url.searchParams.set('latitude', location.latitude);
    url.searchParams.set('longitude', location.longitude);
    const response = await fetch(url);
    if (!response.ok) throw new Error('Soil temperature is temporarily unavailable.');
    const data = await response.json();
    const variable = 'soil_temperature_0_to_10cm';
    const times = data.hourly?.time || [];
    const values = (data.hourly?.[variable] || []).map(Number);
    const currentValue = Number(data.current?.[variable]);
    const currentTime = data.current?.time;
    if (!Number.isFinite(currentValue) || !times.length || !values.length || !currentTime) throw new Error('Soil temperature is temporarily unavailable.');
    let currentIndex = -1;
    for (let index = times.length - 1; index >= 0; index -= 1) {
      if (times[index] <= currentTime) { currentIndex = index; break; }
    }
    if (currentIndex < 0) throw new Error('Soil temperature is temporarily unavailable.');
    const history = values.slice(0, currentIndex + 1).filter(Number.isFinite);
    if (history.length < 240) throw new Error('Soil temperature history is temporarily unavailable.');
    const fiveDay = mean(history.slice(-120));
    const previousFiveDay = mean(history.slice(-240, -120));
    const recent = mean(history.slice(-72));
    const prior = mean(history.slice(-144, -72));
    const trend = recent > prior + 0.6 ? 'Warming' : recent < prior - 0.6 ? 'Cooling' : 'Steady';
    const daily = new Map();
    times.slice(0, currentIndex + 1).forEach((time, index) => {
      const value = values[index];
      if (!Number.isFinite(value)) return;
      const date = time.slice(0, 10);
      const bucket = daily.get(date) || [];
      bucket.push(value);
      daily.set(date, bucket);
    });
    return {
      current: currentValue,
      average: fiveDay,
      previousAverage: previousFiveDay,
      trend,
      series: [...daily].map(([date, dayValues]) => ({ date, value: Number(mean(dayValues).toFixed(1)) })).slice(-30)
    };
  };

  const loadForLocation = async (location) => {
    const key = `${location?.latitude},${location?.longitude}`;
    if (!key || key === 'undefined,undefined') throw new Error('A valid location is required.');
    if (!pending.has(key)) pending.set(key, getSoilData(location).finally(() => pending.delete(key)));
    return pending.get(key);
  };

  window.LSSoilTemperature = { geocodeZip, getSoilData, loadForLocation };
})();
